import { EmailTemplateFormData } from "../types";
import {
  TemplateEditorLocaleObject,
  useEmailEditorLocale,
} from "../context/templates-editor-locale-context";
import { Block } from "@babackapp/blocks-editor/types";
import { isBlockTranslated } from "@babackapp/editor-blocks-library/blocks";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";

type TemplateValidationItem = {
  type: "missing_translation" | "missing_subject_translation";
  severity: "error" | "warning";
  blockId?: string;
};

export default function useTemplateValidation() {
  const { availableLocales, defaultLocale } = useEmailEditorLocale();
  const { subjectManagedByWorkflow } = useTemplatesEditorConfig();

  function validateTemplate(
    templateData: EmailTemplateFormData,
    options?: { validateSubject?: boolean; hiddenBlockIds?: string[] },
  ) {
    // In v2 (subjectManagedByWorkflow) the standalone template editor hides the
    // subject field, so we skip the check by default. The workflow customization
    // editor, where the workflow subject IS being edited, forces it back on via
    // `validateSubject: true`.
    const shouldValidateSubject =
      options?.validateSubject ?? !subjectManagedByWorkflow;
    // Blocks hidden in the current workflow won't be rendered in the email, so
    // their missing translations are irrelevant — skip them.
    const hiddenBlockIds = new Set(options?.hiddenBlockIds ?? []);

    const validationItems: TemplateValidationItem[] = [
      ...validateBlocksTranslation(
        templateData.content.beforeBlocks,
        availableLocales,
        defaultLocale,
        hiddenBlockIds,
      ),
      ...validateBlocksTranslation(
        templateData.content.insideBlocks,
        availableLocales,
        defaultLocale,
        hiddenBlockIds,
      ),
      ...validateBlocksTranslation(
        templateData.content.afterBlocks,
        availableLocales,
        defaultLocale,
        hiddenBlockIds,
      ),
    ];
    if (shouldValidateSubject) {
      availableLocales.forEach((locale) => {
        if (!(templateData.subject[locale.value] as string)?.length) {
          validationItems.push({
            type: "missing_subject_translation",
            severity: locale.value === defaultLocale ? "error" : "warning",
          });
        }
      });
    }

    return validationItems;
  }

  return {
    validateTemplate,
  };
}

function validateBlocksTranslation(
  blocks: Block[],
  availableLocales: TemplateEditorLocaleObject[],
  defaultLocale: string,
  hiddenBlockIds?: Set<string>,
) {
  const validationItems: TemplateValidationItem[] = [];

  blocks.forEach((block) => {
    if (hiddenBlockIds?.has(block.id)) return;
    availableLocales.forEach((locale) => {
      if (!isBlockTranslated(locale.value, block)) {
        validationItems.push({
          type: "missing_translation",
          severity: locale.value === defaultLocale ? "error" : "warning",
          blockId: block.id,
        });
      }
    });
  });

  return validationItems;
}
